import type { Event } from 'models';
import { Empty, Icon, InfinityLoading, Skeleton } from 'ui';
import { useDebounce } from 'common/hooks';
import { EventInviteesListContentWrapper } from '../../../../../components';
import { useEventParticipantsInfiniteQuery } from '../../../../../queries';
import type { EventInviteesFiltersType } from '../event-invitees-filters';
import { EventDistributionList } from './event-distribution-list';
import { EventInvitee } from './event-invitee';
import { useEventInviteesActions } from './event-invitee/hooks';

type P = {
  event: Event;
  filter: {
    search?: string;
    type?: EventInviteesFiltersType;
  };
};

const EventInviteesList = ({ event, filter: { search: propertySearch, type } }: P) => {
  const { isDebouncing, value: search } = useDebounce(propertySearch);
  const { contextHolder, getInviteesActions, isLoadingAction } = useEventInviteesActions();
  const {
    data: allInviteesData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useEventParticipantsInfiniteQuery({
    filter: { eventId: event.id, search },
    pagination: { pageSize: 10 },
  });

  const allInvitees = allInviteesData?.pages.flat().flatMap((inviteesPaginated) => inviteesPaginated.data);

  return (
    <>
      {isLoading || isDebouncing || !allInvitees ? (
        <Skeleton active paragraph={{ rows: 14 }} title={false} />
      ) : (
        <EventInviteesListContentWrapper>
          {allInvitees.length === 0 ? (
            <Empty
              description={{
                subtitle: 'Try refining your search or adjusting the filters',
                title: 'No results found',
              }}
              image={<Icon icon="inbox-line" />}
            />
          ) : (
            <>
              {type === 'community'
                ? event.distributionList?.map((distribution) => (
                    <EventDistributionList distribution={distribution} key={distribution.communityId} />
                  ))
                : allInvitees.map((invitee) => (
                    <EventInvitee
                      event={event}
                      getInviteesActions={getInviteesActions}
                      isLoadingAction={isLoadingAction}
                      key={invitee.ref}
                      participant={invitee}
                    />
                  ))}

              <InfinityLoading
                hasMoreData={hasNextPage}
                isLoading={isFetching}
                onEnter={fetchNextPage}
                spinnerSize="small"
              />
            </>
          )}
        </EventInviteesListContentWrapper>
      )}
      {contextHolder}
    </>
  );
};

export default EventInviteesList;
